import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DateTime } from "luxon";
import { BehaviorSubject, forkJoin, Subject } from "rxjs";
import { finalize, map, switchMap, takeUntil } from "rxjs/operators";
import { IProject } from "src/app/admin/projects/shared/models/IProject";
import { ProjectService } from "src/app/admin/shared/services/project.service";
import { ModalRouteService } from "src/app/shared/services/modal-route.service";
import { TimePeriodsService } from "src/app/time-periods/time-periods.service";
import { TimetrackingService } from "../timetracking.service";

@Component({
  selector: "app-time-list",
  templateUrl: "./time-list.component.html",
  styleUrls: ["./time-list.component.scss"],
})
export class TimeListComponent implements OnInit, OnDestroy {
  private destroy = new Subject();
  private loadingSubject = new BehaviorSubject<boolean>(true);

  loading$ = this.loadingSubject.asObservable();

  private userId: number;
  private projectId: number;

  workDay: DateTime;
  project: IProject;
  canAdd: boolean;
  canEdit: boolean;

  constructor(
    private modal: ModalRouteService,
    private activatedRoute: ActivatedRoute,
    projectService: ProjectService,
    timeTrackingService: TimetrackingService,
    timePeriodService: TimePeriodsService
  ) {
    activatedRoute.queryParams
      .pipe(
        takeUntil(this.destroy),
        switchMap((params) => {
          this.userId = parseInt(params.userid, 10);
          this.projectId = parseInt(params.projectid, 10) || null;
          const workdayString = params.workday;
          if (workdayString) {
            this.workDay = DateTime.fromISO(params.workday);
          } else {
            this.workDay = DateTime.local();
          }

          this.canAdd = timeTrackingService.canAddOnDay(this.workDay);

          if (!this.projectId) {
            // if there isnt a project id we should prbably throw an error
          }
          this.loadingSubject.next(true);
          const canEdit$ = timePeriodService
            .getTimePeriodExceptions(this.workDay, this.workDay)
            .pipe(
              map((timePeriods) => {
                // There should be only one time period for this one day
                const timePeriod = timePeriods[0];
                if (timePeriod) {
                  const isLocked = !!timePeriod.locked;
                  const hasException = !!timePeriod.timePeriodExceptions.find(
                    (e) => e.userId === this.userId
                  );
                  return !isLocked || (isLocked && hasException);
                }
                return true;
              })
            );
          return forkJoin([
            canEdit$,
            projectService.getProject(this.projectId),
            timeTrackingService.getUserHoursForProject(
              this.userId,
              this.projectId,
              this.workDay
            ),
          ]).pipe(finalize(() => this.loadingSubject.next(false)));
        })
      )
      .subscribe(([canEdit, project, entries]) => {
        this.canEdit = canEdit;
        project.timeEntries = entries;
        this.project = project;
      });
  }

  ngOnInit() {}
  ngOnDestroy() {
    this.destroy.next();
  }

  viewPrevDay() {
    this.modal.changeModalParams(this.activatedRoute, {
      workday: this.workDay.minus({ days: 1 }),
    });
  }
  viewNextDay() {
    this.modal.changeModalParams(this.activatedRoute, {
      workday: this.workDay.plus({ days: 1 }),
    });
  }
  addEntry() {
    this.modal.navigate("modal/time/add");
  }
  editEntry(entryId: number) {
    this.modal.navigate(`modal/time/edit/${entryId}`);
  }
  deleteEntry(entryId: number) {
    this.modal.navigate(`modal/time/delete/${entryId}`);
  }

  close() {
    this.modal.closeModal();
  }
}
